import { Uri, Type, Loc, Extension } from 'main.core';
import { defineStore } from 'ui.vue3.pinia';
import { markRaw } from 'ui.vue3';
import { OauthFailed } from '../notices/oauth-failed';
import { UnknownPortal } from '../notices/unknown-portal';
import { IncorrectInvite } from '../notices/incorrect-invite';
import { InviteCancelled } from '../notices/invite-cancelled';
import { WrongInviteToken } from '../notices/wrong-invite-token';
import { InvitesDisabled } from '../notices/invites-disabled';
import { CantSendConfirmationEmail } from '../notices/cant-send-confirmation-email';
import {
	UserAccount,
	sharedStoreState,
	sharedStoreActions,
	HtmlToTextConverter,
} from 'b24network.vue.cross-scenario-shared-js';
import { UserEmailNotConfirmed } from '../notices/user-email-not-confirmed';
import { UserNotJoinedToPortal } from '../notices/user-not-joined-to-portal';
import { NonSsoUserOpensSsoPortal } from '../notices/non-sso-user-opens-sso-portal';
import { InviteNotConfirmedByAdmin } from '../notices/invite-not-confirmed-by-admin';
import { requestPasswordRestoreData, isPasswordRestoreCaptchaPrefetchError } from '../services/password-restore-service';
import {
	InviteIsForAnotherUser,
	UserNotExistsOnPortal,
} from 'b24network.vue.invite-shared';

const settings = Extension.getSettings('b24network.vue.logging-in-scenario');

export const useLoggingInStore = defineStore('loggingInStore', {
	state: () => ({
		...sharedStoreState,

		// When collab name is not empty, we are in collab invitation scenario
		collabName: '',
		qrCodeLink: null,
		qrCodeConfig: {},
		userCheckWord: '',
		// A portal current user is entering to
		enteredPortal: null,
		// An notice for a user from any page to be shown on SignInNoticesShowing page
		userNotice: null,
		forceSsoSignIn: false,
		loggingInUserId: null,
		currentUserPortals: [],
		isPageContentHidden: false,
		// A login, passed through GET query parameter from another extension, to restore password
		passwordRestoreLogin: '',
		isSigningInToSsoPortal: false,
		// Information about a user for whom we may need to set up a password the first time
		passwordSetUpUserDetails: null,
		// Indicates that user uses regular logging in way for portal with SSO-only logging in
		isNonSsoLoggingInForSsoPortal: false,
		redirectToPortalsListWhenAuthorized: false,
		notYetAcceptedInvites: [],
		shouldShowEmptyPortalListWarning: false,
		shouldShowCreateButtonOnAuthPage: settings.shouldShowCreateButtonOnAuthPage ?? false,
		confirmationCallPullConfig: settings.confirmationCallPullConfig ?? null,
		receiveWebinarsText: settings.receiveWebinarsText ?? '',
		isDesktopLogin: false,
		passwordRestorePrefetch: null,
		passwordRestorePrefetchCaptchaFailure: null,
		webLoginCaptchaSettings: {
			isEnabled: false,
			captchaType: '',
		},
		showVibeAuthPromoBlock: false,
		vibecodeContinueTermsHtml: '',
		legalConsentSign: '',
	}),

	getters: {
		pageHeadingHtml(state): string
		{
			if (state.collabName)
			{
				return Loc.getMessage(
					'EXT_B24NET_LOGGING_IN_USER_JOIN_TO_COLLAB',
					{
						'[b]': '<b>',
						'[br]': '<br>',
						'[/b]': '</b>',
						'#COLLAB_NAME#': state.collabName,
					},
				);
			}

			return Loc.getMessage(
				'EXT_B24NET_LOGGING_IN_JOIN_OUR_TEAM',
				{
					'[b]': '<b>',
					'[br]': '<br>',
					'[/b]': '</b>',
					'#PORTAL_NAME#': state.enteredPortal?.host ?? '',
				},
			);
		},

		pageHeadingTitle(state): string
		{
			return HtmlToTextConverter.convert(this.pageHeadingHtml);
		},

		isRedirectAfterScanQrAllowed(): Boolean
		{
			return this.qrCodeConfig.allowRedirect === true;
		},
	},

	actions: {
		...sharedStoreActions,

		setUserNotice(notice: ?Object): void
		{
			this.userNotice = notice ? markRaw(notice) : null;
		},

		prependRawAccountToHistory(rawAccount: Object): void
		{
			this.accountsHistory.unshift(markRaw(UserAccount.makeFromRawAccountData(rawAccount)));
		},

		updateCurrentUserPortals(rawPortals: Array): void
		{
			this.currentUserPortals = rawPortals;
		},

		hasAccountInHistory(accountLogin): boolean
		{
			return this.findAccountInHistoryByLogin(accountLogin) !== null;
		},

		isAccountConfirmed(accountLogin: string): boolean
		{
			return this.findAccountInHistoryByLogin(accountLogin)?.isConfirmed() ?? false;
		},

		updateUserNoticeFromInternalCode(internalErrorCode: string, additionalInfo: string = null): Error
		{
			switch (internalErrorCode)
			{
				// User not exists on a portal and wasn't invited yet
				case 'OAUTH_ERROR_PROFILE_INACTIVE':
				case 'OAUTH_ERROR_PROFILE_NOT_FOUND':
				case 'OAUTH_ERROR_PROFILE_NOT_FOUND_JOIN':
					this.setUserNotice(new UserNotExistsOnPortal(
						this.enteredPortal.host,
						this.loggedInUserAccount,
					));
					break;

				// Secret error codes when try to send confirmation email
				case 'ERROR_SEND_CONFIRMATION':
					this.setUserNotice(new CantSendConfirmationEmail(additionalInfo));
					break;

				// Invite hasn't accepted by a user yet
				case 'OAUTH_ERROR_PROFILE_NOT_ACCEPTED':
					this.setUserNotice(new UserNotJoinedToPortal(
						this.enteredPortal.host,
						this.loggedInUserAccount,
						this.notYetAcceptedInvites,
					));
					break;

				// Portal admin hasn't confirmed user joining yet
				case 'OAUTH_ERROR_PROFILE_NOT_CONFIRMED':
					this.setUserNotice(new InviteNotConfirmedByAdmin(
						this.loggedInUserAccount.toLogin(),
					));
					break;

				// Email is not confirmed when attempting to go to a portal
				case 'OAUTH_ERROR_UNCONFIRMED':
					this.setUserNotice(new UserEmailNotConfirmed(this.loggedInUserAccount));
					break;

				case 'INVITE_IS_FOR_ANOTHER_USER':
					this.setUserNotice(new InviteIsForAnotherUser(
						this.enteredPortal.host,
						this.loggedInUserAccount,
					));
					break;

				// Authorized user that not belongs to a SSO portal trying to enter SSO portal
				case 'OAUTH_ERROR_SSO_RESTRICTION_AUTH':
					this.setUserNotice(new NonSsoUserOpensSsoPortal(
						this.enteredPortal.host,
						this.loggedInUserAccount,
					));
					break;

				// Oauth2 authorization error for authorized user
				case 'ERROR_OAUTH':
					this.setUserNotice(new OauthFailed());
					break;

				// Invalid or not existing portal
				case 'OAUTH_ERROR_WRONG_CLIENT':
					this.setUserNotice(new UnknownPortal());
					break;

				case 'OAUTH_ERROR_WRONG_TOKEN':
					this.setUserNotice(new WrongInviteToken());
					break;

				case 'OAUTH_ERROR_INVITE_INACTIVE':
					this.setUserNotice(new InviteCancelled());
					break;

				case 'OAUTH_ERROR_INVITES_DISABLED':
					this.setUserNotice(new InvitesDisabled(
						this.enteredPortal?.host ?? '',
					));
					break;

				case 'OAUTH_ERROR_WRONG_INVITE_REQUEST':
					this.setUserNotice(new IncorrectInvite(
						this.enteredPortal?.host ?? '',
					));
					break;

				default:
					return null;
			}

			return null;
		},

		getPortalUserId(): ?number
		{
			const portalUserId = Number((new Uri(window.location.href)).getQueryParam('profile_id'));

			return (Type.isNumber(portalUserId) && portalUserId > 0) ? portalUserId : null;
		},

		clearPasswordRestorePrefetch(): void
		{
			this.passwordRestorePrefetch = null;
		},

		clearPasswordRestorePrefetchCaptchaFailure(): void
		{
			this.passwordRestorePrefetchCaptchaFailure = null;
		},

		async requestPasswordRestorePrefetch(
			{
				login,
				defaultCountry,
				captchaResult = null,
			}: {
				login: string,
				defaultCountry: string,
				captchaResult?: ?Object,
			},
		): Promise<Object>
		{
			this.clearPasswordRestorePrefetch();

			try
			{
				const prefetch = await requestPasswordRestoreData({
					login,
					defaultCountry,
					captchaResult,
				});
				this.passwordRestorePrefetch = prefetch;

				return prefetch;
			}
			catch (error)
			{
				if (isPasswordRestoreCaptchaPrefetchError(error))
				{
					const captchaError = (error.errorResponse?.errors ?? [])
						.find((err) => err.code === 'ERROR_CAPTCHA')
						?.message ?? ''
					;

					this.passwordRestorePrefetchCaptchaFailure = {
						login: error.login,
						account: error.account,
						captchaError,
					};
					this.clearPasswordRestorePrefetch();
				}

				throw error;
			}
		},
	},
});
